import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { Plus, Trash } from 'lucide-react';
import { formatDate } from '@/utils/helpers';

export const Blacklist = () => {
  const [blacklist, setBlacklist] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ serial: '', reason: '' });
  const { user } = useAuth();

  useEffect(() => {
    loadBlacklist();
  }, []);

  const loadBlacklist = async () => {
    try {
      const res = await api.getBlacklist();
      setBlacklist(res.data);
    } catch (error) {
      console.error('Error loading blacklist:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createBlacklist({
        ...formData,
        added_by: user.full_name
      });
      loadBlacklist();
      setShowModal(false);
      setFormData({ serial: '', reason: '' });
    } catch (error) {
      alert('Failed to add to blacklist');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove from blacklist?')) return;
    try {
      await api.deleteBlacklist(id);
      loadBlacklist();
    } catch (error) {
      alert('Failed to remove from blacklist');
    }
  };

  return (
    <div className="space-y-6" data-testid="blacklist-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Blacklist</h1>
        <Button onClick={() => setShowModal(true)}>
          <Plus size={20} className="mr-2" />
          Add Serial
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Serial Number</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Added By</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {blacklist.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">No blacklisted serials</TableCell>
              </TableRow>
            ) : (
              blacklist.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium font-mono">{item.serial}</TableCell>
                  <TableCell>{item.reason}</TableCell>
                  <TableCell>{item.added_by}</TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                      <Trash size={16} className="text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add to Blacklist">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Serial Number"
            value={formData.serial}
            onChange={(e) => setFormData({ ...formData, serial: e.target.value })}
            required
          />
          <Textarea
            label="Reason"
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            required
            rows={4}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" variant="danger">Add to Blacklist</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
